// Rescan an existing contract's source document. User drops a file,
// the server runs AI extraction, and the modal shows a three-bucket
// diff (new info / conflicting values / unchanged) for the user to
// cherry-pick which fields to apply.
//
// Styled to match the rest of the app — CSS tokens, .card / .btn /
// .callout classes, no Tailwind slate/accent palette.

import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  X,
  Upload,
  Loader2,
  Check,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Plus,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import Modal from './Modal';
import { apiRequest } from '../services/apiClient';
import {
  SCAN_FIELD_LABELS,
  ScanSectionHeader,
  formatScanValue,
} from './ScanDocumentModalParts';

export default function ScanDocumentModal({ isOpen, onClose, contractId, onSuccess, initialFile = null, hasStoredDocument = false }) {
  const [step, setStep] = useState('upload'); // upload | scanning | review | applying
  const [dragOver, setDragOver] = useState(false);
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [selectedAutoFilled, setSelectedAutoFilled] = useState({});
  const [selectedConflicts, setSelectedConflicts] = useState({});
  const [showUnchanged, setShowUnchanged] = useState(false);
  const fileInputRef = useRef(null);

  const resetState = useCallback(() => {
    setStep('upload');
    setDragOver(false);
    setFile(null);
    setError(null);
    setScanResult(null);
    setSelectedAutoFilled({});
    setSelectedConflicts({});
    setShowUnchanged(false);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  // Allow the modal to open with a pre-selected file (e.g. handed off
  // from the contract-detail upload flow's "Re-scan with AI" toggle).
  // Skips the file-picker step and goes straight to scanning.
  useEffect(() => {
    if (!isOpen || !initialFile) return;
    setFile(initialFile);
    setStep('upload'); // brief — handleScan flips to 'scanning'
    // Defer to next tick so the file state is committed before the
    // scan call reads it.
    const t = setTimeout(() => { handleScanRef.current?.(); }, 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialFile]);

  // Ref so the auto-scan effect above can call handleScan without
  // pulling it into the dep list (which would re-run every render).
  const handleScanRef = useRef(null);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files[0];
    if (!dropped) return;
    const ext = dropped.name.split('.').pop().toLowerCase();
    if (!['pdf', 'docx', 'doc'].includes(ext)) {
      setError('Please upload a PDF, DOCX, or DOC file.');
      return;
    }
    setFile(dropped);
    setError(null);
  }, []);

  const handleFileSelect = useCallback((e) => {
    const selected = e.target.files[0];
    if (!selected) return;
    setFile(selected);
    setError(null);
  }, []);

  // overrideFile: a File (the auto-scan handoff), the string 'stored' (rescan
  // the contract's copy of record, no upload — founder ask 2026-08-06), or a
  // click event when used directly as an onClick handler (ignored; falls back
  // to the picked-file state, which the button's disabled state guarantees).
  const handleScan = async (overrideFile = null) => {
    const useStored = overrideFile === 'stored';
    const explicit = typeof File !== 'undefined' && overrideFile instanceof File ? overrideFile : null;
    const target = useStored ? null : (explicit || file);
    if (!useStored && !target) return;
    setStep('scanning');
    setError(null);
    try {
      let body;
      if (!useStored) {
        body = new FormData();
        body.append('file', target);
      }
      const result = await apiRequest(`/contracts/${contractId}/scan`, {
        method: 'POST',
        ...(body ? { body } : {}),
      });
      setScanResult(result);
      const autoSel = {};
      Object.keys(result.autoFilled || {}).forEach((k) => { autoSel[k] = true; });
      setSelectedAutoFilled(autoSel);
      setSelectedConflicts({});
      setStep('review');
    } catch (err) {
      console.error('Scan error:', err);
      setError(err.message);
      setStep('upload');
    }
  };
  // Keep the auto-scan effect's ref in sync with the latest closure.
  handleScanRef.current = () => handleScan(initialFile);

  const handleApply = async () => {
    setStep('applying');
    setError(null);
    try {
      const acceptedFields = {};
      Object.entries(selectedAutoFilled).forEach(([key, sel]) => {
        if (sel && scanResult.autoFilled[key] !== undefined) {
          acceptedFields[key] = scanResult.autoFilled[key];
        }
      });
      Object.entries(selectedConflicts).forEach(([key, sel]) => {
        if (sel && scanResult.conflicts[key]?.extracted !== undefined) {
          acceptedFields[key] = scanResult.conflicts[key].extracted;
        }
      });
      const updatedContract = await apiRequest(`/contracts/${contractId}/scan/apply`, {
        method: 'POST',
        json: { acceptedFields, tempFileName: scanResult.tempFileName },
      });
      onSuccess?.(updatedContract);
      handleClose();
    } catch (err) {
      console.error('Apply error:', err);
      setError(err.message);
      setStep('review');
    }
  };

  const { autoFilledCount, conflictCount, total } = (() => {
    const af = Object.values(selectedAutoFilled).filter(Boolean).length;
    const cf = Object.values(selectedConflicts).filter(Boolean).length;
    return { autoFilledCount: af, conflictCount: cf, total: af + cf };
  })();

  const autoCount = Object.keys(scanResult?.autoFilled || {}).length;
  const conflictTotal = Object.keys(scanResult?.conflicts || {}).length;
  const unchangedCount = Object.keys(scanResult?.unchanged || {}).length;
  const hasNoChanges = scanResult && autoCount === 0 && conflictTotal === 0;
  const modalDescription = {
    upload: 'Upload a document to extract fields and refresh this contract.',
    scanning: 'Analyzing the document with AI…',
    review: 'Pick which extracted values to apply.',
    applying: 'Saving your selections…',
  }[step];

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Scan document"
      description={modalDescription}
      icon={Sparkles}
      size="lg"
      contentStyle={{ maxWidth: 640 }}
      footerJustify="space-between"
      footer={(
        <>
          {step === 'review' && !hasNoChanges ? (
            <div className="muted" style={{ fontSize: 12 }}>
              {(() => {
                const parts = [];
                if (autoFilledCount > 0) parts.push(`${autoFilledCount} new`);
                if (conflictCount > 0) parts.push(`${conflictCount} updated`);
                return parts.length > 0 ? `${parts.join(', ')} selected` : 'Nothing selected';
              })()}
            </div>
          ) : (
            <span />
          )}

          <div className="row" style={{ gap: 8 }}>
            <button
              onClick={handleClose}
              disabled={step === 'scanning' || step === 'applying'}
              className="btn outline sm"
            >
              Cancel
            </button>

            {step === 'upload' && (
              <button
                onClick={handleScan}
                disabled={!file}
                className="btn primary sm"
              >
                <Sparkles className="w-3.5 h-3.5" />
                Scan
              </button>
            )}

            {step === 'review' && !hasNoChanges && (
              <button
                onClick={handleApply}
                disabled={total === 0}
                className="btn primary sm"
              >
                <Check className="w-3.5 h-3.5" />
                Apply changes
              </button>
            )}

            {step === 'review' && hasNoChanges && (
              <button onClick={handleClose} className="btn primary sm">
                Done
              </button>
            )}
          </div>
        </>
      )}
    >
      <div
        style={{
          flex: 1, overflowY: 'auto',
          padding: '16px 20px',
          display: 'flex', flexDirection: 'column', gap: 14,
        }}
      >
          {step === 'upload' && (
            <>
              {hasStoredDocument && (
                <>
                  {/* Founder ask 2026-08-06: the copy of record is already in
                      Docket — re-running the field scan shouldn't demand a
                      re-upload. Backend sources the executed-else-latest
                      document itself. */}
                  <button
                    type="button"
                    className="btn outline"
                    style={{ width: '100%', justifyContent: 'center' }}
                    onClick={() => handleScan('stored')}
                  >
                    <Sparkles className="w-4 h-4" />
                    Rescan the current document on file
                  </button>
                  <div className="muted" style={{ fontSize: 11.5, textAlign: 'center' }}>
                    or scan a different file
                  </div>
                </>
              )}
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: `1.5px dashed ${dragOver ? 'var(--color-purple-500)' : 'var(--border-primary)'}`,
                  background: dragOver ? 'var(--surface-purple)' : 'var(--surface-base)',
                  borderRadius: 10, padding: '32px 20px',
                  textAlign: 'center', cursor: 'pointer',
                  transition: 'background 120ms, border-color 120ms',
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.doc"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <div
                  style={{
                    width: 44, height: 44, borderRadius: 999,
                    background: 'var(--surface-secondary)',
                    display: 'grid', placeItems: 'center',
                    margin: '0 auto 12px',
                  }}
                >
                  <Upload className="w-5 h-5" style={{ color: 'var(--icon-secondary)' }} />
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 450 }}>
                  Drop a file here or click to browse
                </div>
                <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                  PDF, DOCX, or DOC · up to 50 MB
                </div>
              </div>

              {file && (
                <div
                  className="row"
                  style={{
                    gap: 10, padding: 10,
                    border: '0.5px solid var(--border-primary)',
                    borderRadius: 8,
                    background: 'var(--surface-base)',
                  }}
                >
                  <div
                    style={{
                      width: 30, height: 36, borderRadius: 4,
                      background: 'var(--color-red-600)', color: '#fff',
                      display: 'grid', placeItems: 'center',
                      fontSize: 9, fontWeight: 600, letterSpacing: 0.5,
                    }}
                  >
                    {(file.name.split('.').pop() || 'DOC').toUpperCase().slice(0, 4)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{ fontSize: 13, fontWeight: 450, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {file.name}
                    </div>
                    <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); setFile(null); }}
                    className="icon-btn xs"
                    title="Remove"
                    aria-label="Remove file"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}

              {error && (
                <div className="callout red">
                  <div className="callout-icon"><AlertTriangle className="w-4 h-4" /></div>
                  <div className="callout-body"><div style={{ fontSize: 12 }}>{error}</div></div>
                </div>
              )}
            </>
          )}

          {step === 'scanning' && (
            <div style={{ padding: '48px 0', textAlign: 'center' }}>
              <Loader2
                className="w-6 h-6 animate-spin"
                style={{ margin: '0 auto 12px', color: 'var(--text-purple)' }}
              />
              <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>Scanning document…</div>
              <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                Extracting contract fields with Claude
              </div>
            </div>
          )}

          {step === 'applying' && (
            <div style={{ padding: '48px 0', textAlign: 'center' }}>
              <Loader2
                className="w-6 h-6 animate-spin"
                style={{ margin: '0 auto 12px', color: 'var(--text-purple)' }}
              />
              <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>Applying changes…</div>
            </div>
          )}

          {step === 'review' && scanResult && (
            <>
              {scanResult.summary && (
                <div
                  style={{
                    padding: 12, borderRadius: 8,
                    background: 'var(--surface-panel)',
                    border: '0.5px solid var(--border-primary)',
                  }}
                >
                  <div className="muted" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>
                    Summary · {scanResult.confidence || 'medium'} confidence
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5 }}>
                    {scanResult.summary}
                  </div>
                </div>
              )}

              {hasNoChanges && (
                <div style={{ padding: '36px 0', textAlign: 'center' }}>
                  <div
                    style={{
                      width: 44, height: 44, borderRadius: 999,
                      background: 'var(--surface-green)', color: 'var(--text-green)',
                      display: 'grid', placeItems: 'center',
                      margin: '0 auto 10px',
                    }}
                  >
                    <Check className="w-5 h-5" />
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>Nothing new to apply.</div>
                  <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                    The document matches what's already on this contract.
                  </div>
                </div>
              )}

              {autoCount > 0 && (
                <ScanSectionHeader
                  icon={<Plus className="w-3.5 h-3.5" style={{ color: 'var(--text-green)' }} />}
                  title="New fields"
                  count={autoCount}
                />
              )}
              {autoCount > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {Object.entries(scanResult.autoFilled).map(([key, value]) => {
                    const selected = !!selectedAutoFilled[key];
                    return (
                      <label
                        key={key}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: 10, borderRadius: 8, cursor: 'pointer',
                          border: `0.5px solid ${selected ? 'var(--color-green-500)' : 'var(--border-primary)'}`,
                          background: selected ? 'var(--surface-green)' : 'var(--surface-base)',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={(e) => setSelectedAutoFilled((prev) => ({ ...prev, [key]: e.target.checked }))}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="muted" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                            {SCAN_FIELD_LABELS[key] || key}
                          </div>
                          <div
                            style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 450, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          >
                            {formatScanValue(key, value)}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}

              {conflictTotal > 0 && (
                <ScanSectionHeader
                  icon={<AlertTriangle className="w-3.5 h-3.5" style={{ color: 'var(--text-yellow)' }} />}
                  title="Different values"
                  count={conflictTotal}
                />
              )}
              {conflictTotal > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {Object.entries(scanResult.conflicts).map(([key, { current, extracted }]) => {
                    const selected = !!selectedConflicts[key];
                    return (
                      <label
                        key={key}
                        style={{
                          display: 'flex', alignItems: 'flex-start', gap: 10,
                          padding: 10, borderRadius: 8, cursor: 'pointer',
                          border: `0.5px solid ${selected ? 'var(--color-yellow-500)' : 'var(--border-primary)'}`,
                          background: selected ? 'var(--surface-yellow)' : 'var(--surface-base)',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={(e) => setSelectedConflicts((prev) => ({ ...prev, [key]: e.target.checked }))}
                          style={{ marginTop: 3 }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="muted" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>
                            {SCAN_FIELD_LABELS[key] || key}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 8, alignItems: 'center' }}>
                            <div
                              style={{
                                padding: 8, borderRadius: 6,
                                background: 'var(--surface-panel)',
                                border: '0.5px solid var(--border-primary)',
                                minWidth: 0,
                              }}
                            >
                              <div className="muted" style={{ fontSize: 10, marginBottom: 2 }}>Current</div>
                              <div
                                style={{ fontSize: 12, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              >
                                {formatScanValue(key, current)}
                              </div>
                            </div>
                            <ArrowRight
                              className="w-3 h-3"
                              style={{ color: selected ? 'var(--text-yellow)' : 'var(--icon-tertiary)' }}
                            />
                            <div
                              style={{
                                padding: 8, borderRadius: 6,
                                background: selected ? 'var(--surface-yellow)' : 'var(--surface-base)',
                                border: `0.5px solid ${selected ? 'var(--color-yellow-500)' : 'var(--border-primary)'}`,
                                minWidth: 0,
                              }}
                            >
                              <div className="muted" style={{ fontSize: 10, marginBottom: 2 }}>From document</div>
                              <div
                                style={{
                                  fontSize: 12,
                                  color: selected ? 'var(--text-yellow)' : 'var(--text-primary)',
                                  fontWeight: selected ? 500 : 400,
                                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}
                              >
                                {formatScanValue(key, extracted)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}

              {unchangedCount > 0 && (
                <div>
                  <button
                    onClick={() => setShowUnchanged((v) => !v)}
                    className="btn ghost xs"
                    style={{ padding: '0 4px' }}
                  >
                    {showUnchanged ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    {unchangedCount} field{unchangedCount === 1 ? '' : 's'} already up to date
                  </button>
                  {showUnchanged && (
                    <div
                      style={{
                        marginTop: 8, padding: 10, borderRadius: 8,
                        background: 'var(--surface-panel)',
                        border: '0.5px solid var(--border-primary)',
                      }}
                    >
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 12px', fontSize: 12 }}>
                        {Object.entries(scanResult.unchanged).map(([key, value]) => (
                          <div key={key}>
                            <span className="muted">{SCAN_FIELD_LABELS[key] || key}:</span>{' '}
                            <span style={{ color: 'var(--text-primary)' }}>{formatScanValue(key, value)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div className="callout red">
                  <div className="callout-icon"><AlertTriangle className="w-4 h-4" /></div>
                  <div className="callout-body"><div style={{ fontSize: 12 }}>{error}</div></div>
                </div>
              )}
            </>
          )}
      </div>

    </Modal>
  );
}
